// ViewState persistence keyed by an opaque scopeKey (ADR D4/P0.3).
//
// A scopeKey identifies a task surface instance, e.g.:
//   "project:42"  — a project's task views
//   "me:tasks"    — the cross-project "Meine Aufgaben" hub
//   "list:7"      — a personal list
// This supersedes the old per-project `loadGroupBy` and the global hidden-columns
// store; both are folded into one persisted ViewState per scope.

import {
  DEFAULT_VIEW_STATE,
  type RecordColumnId,
  type FilterRule,
  type GroupBy,
  type SortKey,
  type SortRule,
  type ViewKind,
  type ViewState,
} from "./viewState";

const STORAGE_PREFIX = "juuplan:view:";

const VIEW_KINDS: ViewKind[] = ["list", "board", "calendar", "gantt"];
const GROUP_BYS: GroupBy[] = ["none", "phase", "status", "priority", "assignee", "due", "project", "type"];
const SORT_KEYS: SortKey[] = ["manual", "title", "due", "priority", "status", "created", "updated"];
const COLUMN_IDS: RecordColumnId[] = ["status", "assignees", "labels", "start", "end", "priority", "project", "ticket"];

function oneOf<T extends string>(v: unknown, allowed: T[]): v is T {
  return typeof v === "string" && (allowed as string[]).includes(v);
}

function sanitizeSort(v: unknown): SortRule[] {
  if (!Array.isArray(v)) return [...DEFAULT_VIEW_STATE.sort];
  const out: SortRule[] = [];
  for (const r of v) {
    if (r && typeof r === "object" && oneOf((r as SortRule).key, SORT_KEYS)) {
      const dir = (r as SortRule).dir === "desc" ? "desc" : "asc";
      out.push({ key: (r as SortRule).key, dir });
    }
  }
  return out.length > 0 ? out : [...DEFAULT_VIEW_STATE.sort];
}

function sanitizeFilters(v: unknown): FilterRule[] {
  if (!Array.isArray(v)) return [];
  const out: FilterRule[] = [];
  for (const r of v) {
    if (r && typeof r === "object" && typeof (r as FilterRule).field === "string" && typeof (r as FilterRule).op === "string") {
      out.push({ field: (r as FilterRule).field, op: (r as FilterRule).op, value: (r as FilterRule).value });
    }
  }
  return out;
}

function sanitizeColumns(v: unknown): RecordColumnId[] {
  if (!Array.isArray(v)) return [];
  const out: RecordColumnId[] = [];
  for (const c of v) if (oneOf(c, COLUMN_IDS)) out.push(c);
  return out;
}

/**
 * Coerce an arbitrary parsed blob into a valid ViewState, falling back to
 * `defaults` (or DEFAULT_VIEW_STATE) field-by-field. Never throws.
 */
export function coerceViewState(raw: unknown, defaults: ViewState = DEFAULT_VIEW_STATE): ViewState {
  if (!raw || typeof raw !== "object") return { ...defaults, sort: [...defaults.sort] };
  const o = raw as Record<string, unknown>;
  return {
    view: oneOf(o.view, VIEW_KINDS) ? (o.view as ViewKind) : defaults.view,
    groupBy: oneOf(o.groupBy, GROUP_BYS) ? (o.groupBy as GroupBy) : defaults.groupBy,
    sort: o.sort === undefined ? [...defaults.sort] : sanitizeSort(o.sort),
    filters: o.filters === undefined ? [...defaults.filters] : sanitizeFilters(o.filters),
    hiddenColumns: o.hiddenColumns === undefined ? [...defaults.hiddenColumns] : sanitizeColumns(o.hiddenColumns),
  };
}

/** Load the persisted ViewState for a scope, merged over `defaults`. */
export function loadViewState(scopeKey: string, defaults: ViewState = DEFAULT_VIEW_STATE): ViewState {
  if (typeof localStorage === "undefined") return { ...defaults, sort: [...defaults.sort] };
  const raw = localStorage.getItem(`${STORAGE_PREFIX}${scopeKey}`);
  if (!raw) return { ...defaults, sort: [...defaults.sort] };
  try {
    return coerceViewState(JSON.parse(raw) as unknown, defaults);
  } catch {
    return { ...defaults, sort: [...defaults.sort] };
  }
}

/** Persist the ViewState for a scope. */
export function saveViewState(scopeKey: string, state: ViewState): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(`${STORAGE_PREFIX}${scopeKey}`, JSON.stringify(state));
}

/** Drop the persisted ViewState for a scope (resets to defaults on next load). */
export function clearViewState(scopeKey: string): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(`${STORAGE_PREFIX}${scopeKey}`);
}
